import { z } from 'zod';
import { idSchema } from './content-primitives.ts';
import { jsonPointerSchema, versionedPatchFields } from './json-patch.ts';

export const stringPatchDiffDescription = `A diff hunk starts with a header line (@@ or @@ <anchor text>), followed by context, removed, and added lines. Prefix content lines with space (unchanged context), - (remove), or + (add). Everything after the prefix is literal, including whitespace. Empty lines need their prefix. Each hunk needs at least one prefixed line; the diff must add or remove a line.

Kept/removed lines must match one contiguous sequence exactly and uniquely. Use complete lines, not substrings or patterns. Match the original string in source order without overlapping matched lines, including context. Searches resume after the previous match; later hunks cannot match earlier additions.

An optional @@ <existing line> header advances the search past an exact, unique full-line match in the remaining text. Use one separator space. Consecutive anchors advance in order; anchor lines stay unchanged.

Put *** End of String alone after the final hunk to require a match through the original string's end. A hunk of only + lines needs an anchor or this marker, unless the string is empty or previous matches reached its end. Otherwise, include unchanged context around insertions.

Example:
\`\`\`
@@
 <section>
-  <h2>Welcome</h2>
+  <h2>Welcome home</h2>
 </section>
\`\`\``;

export const stringPatchSchema = z.strictObject({
  type: z
    .enum(['Page', 'Settings', 'Form', 'FormSubmissionAction', 'Asset'])
    .describe('Page includes posts. For Settings, use the site ID.'),
  id: idSchema,
  expectedVersionId: versionedPatchFields.expectedVersionId,
  path: jsonPointerSchema.describe('RFC 6901 JSON Pointer to one existing editable string.'),
  diff: z.string().describe(stringPatchDiffDescription),
});
export type StringPatch = z.infer<typeof stringPatchSchema>;
export const stringPatchResultSchema = z.strictObject({
  versionId: idSchema,
});

export const stringPatchTool = {
  name: 'patch_string_field',
  title: 'Patch a string field',
  description:
    'Edit a string field. Returns { versionId } with the new version. Saves a draft without publishing.',
  schema: stringPatchSchema,
  readOnly: false,
  idempotent: false,
} as const;
